import React, { useEffect, useState } from "react";

import SelectPart from "./SelectPart";
import Icon from "../../common/Icon";
import Checkbox from "../../common/Checkbox";
import LoadingTable from "./ElementsTable/LoadingTable";

import { FONT_SIZE } from "../../../constants/Styles";
import {
  Board,
  BoardContext,
  boardHasBeenPaidFor,
  BOM_PROCESSING_STATES,
  QuoteState,
  SelectedElement,
} from "../../../reducers/uploadedBoard";
import * as styledElements from "./BillOfMaterialsStyles";
import ElementsTable from "./ElementsTable";
import { useBoardFromContext } from "../../../utils/hooks/commonly-stored/uploaded-board/board";
import { useAppSelector } from "../../../utils/hooks/store";
import AdditionalTools from "./AdditionalTools";
import { SelectContainer, SelectOption } from "../../common/FancySelect";
import Modal from "../../common/Modal";
import Button from "../../common/Button";
import { ButtonContainer, Text } from "./UploadBOMFiles/UploadBOMFileStyles";
import UploadBOMFiles from "./UploadBOMFiles";
import { ADMIN_GROUP, ENGINEER_GROUP } from "../../../utils/group";
import { DEMO_BOARD_SLUG } from "../../../constants/boards";
import { useRefreshBillOfMaterialsInStore } from "../../../utils/hooks/commonly-stored/uploaded-board/updates/billOfMaterials";
import {
  createGroupedElements,
  GroupedElements,
  SortingOption,
  useGetElementAlert,
  useSortElements,
} from "../../../utils/hooks/commonly-stored/uploaded-board/billOfMaterials";

const DEFAULT_LOADING_LENGTH = 21;

const BillOfMaterials = () => {
  const { groups } = useAppSelector((state) => state.auth);
  const isAdminOrEngineer =
    groups.includes(ADMIN_GROUP) || groups.includes(ENGINEER_GROUP);
  const board = useBoardFromContext().board as Board;
  const billOfMaterials = useAppSelector(
    (state) =>
      (state.uploadedBoard.boards[board.id] as BoardContext).billOfMaterials,
  );
  const { refreshingBillOfMaterials } = useRefreshBillOfMaterialsInStore();
  const isDemoBoard = board.id == DEMO_BOARD_SLUG;
  const [selectedElement, setSelectedElement] =
    useState<SelectedElement | null>(null);
  const [sortingOption, setSortingOption] = useState(SortingOption.ALL);
  const [groupElements, setGroupElements] = useState(true);
  const elements = billOfMaterials?.elements || [];
  const noElements = billOfMaterials?.elements.length == 0;
  const bomIsProcessing =
    billOfMaterials === null ||
    BOM_PROCESSING_STATES.has(billOfMaterials.state);
  const getElementAlert = useGetElementAlert(board);
  const sortElements = useSortElements(board);

  const bomSortingOptions = Object.values(SortingOption)
    .map((option) => {
      const quantity =
        elements.filter((element) => {
          if (option === SortingOption.ALL) {
            return true;
          } else if (option === SortingOption.POPULATE) {
            return !element.do_not_populate;
          } else if (option === SortingOption.DO_NOT_POPULATE) {
            return element.do_not_populate;
          } else if (option === SortingOption.NEEDS_ATTENTION) {
            if (board.quote.state === QuoteState.ORDER_RECEIVED) {
              return false;
            }
            return getElementAlert(element) !== null;
          }
          return false;
        }).length || 0;
      return {
        option,
        quantity,
      };
    })
    .filter(({ quantity }) => {
      return quantity > 0;
    });

  useEffect(() => {
    if (
      !bomSortingOptions.some((options) => options.option == sortingOption) &&
      bomSortingOptions.length > 0
    ) {
      setSortingOption(bomSortingOptions[0]!.option);
    }
  }, [bomSortingOptions, sortingOption]);

  const groupedElements: GroupedElements[] = createGroupedElements(elements);
  const ungroupedElements = elements.map((element) => ({
    elements: [element],
  }));

  const allElements = sortElements(
    groupElements ? groupedElements : ungroupedElements,
    sortingOption,
  );

  const boardPaidFor = boardHasBeenPaidFor(board);
  const customerCannotEditPurchasedBoard = boardPaidFor && !isAdminOrEngineer;
  const disableEditing = bomIsProcessing || customerCannotEditPurchasedBoard;

  const canBeGrouped = groupedElements.some(
    ({ elements }) => elements.length > 1,
  );

  const canBeSorted = allElements.length > 0;

  const closeModal = () => {
    setSelectedElement(null);
  };

  const loadingTableLength =
    bomIsProcessing || billOfMaterials === null
      ? DEFAULT_LOADING_LENGTH
      : allElements.length;

  return (
    <>
      <Modal isOpen={selectedElement !== null} onRequestClose={closeModal}>
        <SelectPart
          selectedElement={selectedElement as SelectedElement}
          closeModal={closeModal}
          groupElements={groupElements}
        />
      </Modal>
      <styledElements.TableContainer>
        <styledElements.Header>
          <styledElements.HeaderText>
            Bill of Materials
          </styledElements.HeaderText>
          {canBeSorted && (
            <styledElements.Sorting>
              <styledElements.IconContainer>
                <Icon src="/icons/sort.svg" alt="sort-by" /> Sort by:
              </styledElements.IconContainer>
              <SelectContainer>
                {bomSortingOptions.map(({ option, quantity }) => {
                  return (
                    <SelectOption
                      key={option}
                      isSelected={option === sortingOption}
                      onClick={() => setSortingOption(option)}
                      fontSize={FONT_SIZE.size14}
                    >
                      {option} ({quantity})
                    </SelectOption>
                  );
                })}
              </SelectContainer>
            </styledElements.Sorting>
          )}
        </styledElements.Header>
        <styledElements.Header>
          <styledElements.HeaderGroup largeGap>
            {canBeGrouped && (
              <Checkbox
                checked={groupElements}
                onChange={() => setGroupElements((prevState) => !prevState)}
                label="Group matching elements"
              />
            )}
          </styledElements.HeaderGroup>
          <AdditionalTools
            disabled={bomIsProcessing || billOfMaterials === null}
          />
        </styledElements.Header>
        {bomIsProcessing ||
        billOfMaterials === null ||
        refreshingBillOfMaterials ? (
          <LoadingTable
            tableLength={loadingTableLength}
            isAdminOrEngineer={isAdminOrEngineer}
          />
        ) : (
          <>
            {!noElements && (
              <ElementsTable
                allElements={allElements}
                setSelectedElement={setSelectedElement}
                disableEditing={disableEditing}
              />
            )}
            {boardPaidFor && noElements && (
              <styledElements.EmptyBOM>
                No elements on board
              </styledElements.EmptyBOM>
            )}
            {!boardPaidFor && !isDemoBoard && noElements && (
              <>
                <UploadBOMFiles />
                <Text>or</Text>
              </>
            )}
            {(!boardPaidFor || isAdminOrEngineer) && (
              <ButtonContainer>
                <Button
                  label={"Add element to bill of materials"}
                  centerText
                  onClick={() =>
                    !disableEditing && setSelectedElement({ elementId: null })
                  }
                  disabled={disableEditing}
                />
              </ButtonContainer>
            )}
          </>
        )}
      </styledElements.TableContainer>
    </>
  );
};

export default BillOfMaterials;
